import { useAppDispatch, useAppSelector } from '@app/store/store';
import { useCallback } from 'react';

import {
  selectActiveAdvancedEdit,
  selectActiveEditHasUnsavedChanges,
  selectActiveEditSaveIndicator,
  selectCanRedo,
  selectCanUndo,
} from '../selector';

/**
 * Hook for managing undo/redo operations on the active advanced edit.
 * Provides undo/redo functions and state flags.
 *
 * @example
 * const { undo, redo, canUndo, canRedo, hasUnsavedChanges } = useUndoRedo();
 * <button onClick={undo} disabled={!canUndo}>Undo</button>
 */
export const useUndoRedo = () => {
  const dispatch = useAppDispatch();
  const activeEdit = useAppSelector(selectActiveAdvancedEdit);
  const canUndo = useAppSelector(selectCanUndo);
  const canRedo = useAppSelector(selectCanRedo);
  const hasUnsavedChanges = useAppSelector(selectActiveEditHasUnsavedChanges);
  const saveIndicator = useAppSelector(selectActiveEditSaveIndicator);

  const undo = useCallback(() => {
    if (!activeEdit?.id || !canUndo) return;
    dispatch({
      type: '[advanced-editor] undo',
      payload: { editId: activeEdit.id },
    });
  }, [dispatch, activeEdit?.id, canUndo]);

  const redo = useCallback(() => {
    if (!activeEdit?.id || !canRedo) return;
    dispatch({
      type: '[advanced-editor] redo',
      payload: { editId: activeEdit.id },
    });
  }, [dispatch, activeEdit?.id, canRedo]);

  return {
    /**
     * Undo the last edit (if available)
     */
    undo,

    /**
     * Redo the last undone edit (if available)
     */
    redo,

    /**
     * Whether undo is available
     */
    canUndo,

    /**
     * Whether redo is available
     */
    canRedo,

    /**
     * Whether the active edit has unsaved changes
     */
    hasUnsavedChanges,

    /**
     * Comprehensive save status indicator
     * Contains: status, isSaving, isSaved, isFailed, hasUnsavedChanges,
     *           currentVersion, lastSavedVersion, lastSavedAt, lastSavedDraft
     */
    saveIndicator,

    /**
     * The ID of the currently active edit (or null if no edit is active)
     */
    activeEditId: activeEdit?.id ?? null,
  };
};
